import { useEffect, useState } from "react";
import {
    APButton,
    APDialogCard,
    APOtp,
    APRow,
    APSizedBox,
    APText,
    APPalette,
    Deferred,
    getIt,
    handleErrorMessage,
    showSnackbar,
    useCountDown,
} from "ap-components";
import PartnerApiServices from "../../Services/PartnerApiServices";
import { AppDownloadLinkWithUserToken } from "../WebStory/utils/downloadLinkForUsers";

interface AccountVerificationDialogProps {
    deferred: Deferred<string>;
    phoneNumber: string;
    panNumber?: string;
    guardianPan?: string;
    firstName?: string;
    email?: string;
    clientType?: "individual" | "minor" | "nre" | "nro";
    subAdvisorId?: string;
    refId?: string;
    productSource?: string;
    /** Partner-initiated approval link token. When present, creation goes via the
     *  approve flow (appserver approve.js — all side-effects) instead of clientSignup. */
    tkn?: string;
    onOtpResent?: () => void;
}

export default function AccountVerificationDialog({
    deferred,
    phoneNumber,
    panNumber,
    guardianPan,
    firstName,
    email,
    clientType,
    subAdvisorId,
    refId,
    productSource,
    tkn,
    onOtpResent,
}: AccountVerificationDialogProps) {
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);
    const [time, { start }] = useCountDown(30000, 1000);

    // Lockout timer (10 min) — separate from the normal 30s resend countdown
    const [lockoutEndTime, setLockoutEndTime] = useState<number | null>(null);
    const [lockoutRemaining, setLockoutRemaining] = useState(0);

    useEffect(() => {
        if (!lockoutEndTime) return;
        const tick = () => {
            const remaining = lockoutEndTime - Date.now();
            if (remaining <= 0) {
                setLockoutEndTime(null);
                setLockoutRemaining(0);
            } else {
                setLockoutRemaining(remaining);
            }
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [lockoutEndTime]);

    // Clear OTP error when user types — but not if locked out (would wipe the lockout message)
    useEffect(() => {
        if (otpError && lockoutRemaining <= 0) setOtpError("");
    }, [otp]);

    // Auto-clear lockout message when lockout expires
    useEffect(() => {
        if (lockoutRemaining <= 0 && otpError.toLowerCase().startsWith("too many incorrect")) {
            setOtpError("");
        }
    }, [lockoutRemaining]);

    useEffect(() => {
        start();
    }, []);

    function handleLockoutIfNeeded(msg: string): boolean {
        if (msg.toLowerCase().startsWith("too many incorrect attempts")) {
            // Only start lockout timer once — don't reset on repeated attempts
            setLockoutEndTime((prev) => prev ?? Date.now() + 600_000);
            return true;
        }
        return false;
    }

    async function handleResendOTP() {
        if (resending) return;
        setResending(true);
        try {
            await getIt(PartnerApiServices).generateSignupOTP({
                ...(phoneNumber ? { phoneNumber } : {}),
                ...(email ? { email } : {}),
                ...(guardianPan ? { guardianPan } : { panNumber }),
                clientType,
            });
            setOtpError("");
            start();
            onOtpResent?.();
            showSnackbar("OTP Re-sent Successfully");
        } catch (error) {
            const msg = error instanceof Error ? error.message : "";
            if (msg) {
                setOtpError(msg);
                if (!handleLockoutIfNeeded(msg)) {
                    // Rate limited — restart countdown to prevent spam clicks
                    start();
                }
            } else {
                handleErrorMessage(error);
            }
        } finally {
            setResending(false);
        }
    }

    async function handleContinue() {
        if (submitting) return;
        if (otp.length !== 4) {
            setOtpError("Please enter 4-digit OTP");
            return;
        }
        setOtpError("");
        setSubmitting(true);

        try {
            // Partner-initiated approval flow: delegate to appserver approve.js (via
            // core-server), which creates the user + does ALL side-effects (prospect
            // CONVERT/LOST, agenda, dashboard "users"+"userSignup", CRM, zeroboard) and
            // returns the app-download link directly. OTP is validated server-side.
            if (tkn) {
                const res = await getIt(PartnerApiServices).approveClientSignup(tkn, otp);
                deferred.resolve(res.link);
                return;
            }

            const res = await getIt(PartnerApiServices).clientSignup({
                ...(phoneNumber ? { phoneNumber } : {}),
                ...(email ? { email } : {}),
                ...(firstName ? { firstName } : {}),
                ...(guardianPan ? { guardianPan } : { panNumber }),
                otp,
                clientType,
                subAdvisorId,
                refId,
            });

            // Process source (fire-and-forget)
            if (productSource) {
                getIt(PartnerApiServices).processSource(res.token, productSource).catch(() => {});
            }

            const link = AppDownloadLinkWithUserToken(
                subAdvisorId ?? refId ?? "",
                res.token
            );
            deferred.resolve(link);
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : "";
            if (msg) {
                setOtpError(msg);
                handleLockoutIfNeeded(msg);
            } else {
                handleErrorMessage(error);
            }
            setSubmitting(false);
        }
    }

    const isOtpFilled = otp.length === 4;
    const isLockedOut = lockoutRemaining > 0;

    return (
        <APDialogCard
            title="Account Verification"
            description={
                (clientType === "nre" || clientType === "nro") && email
                    ? `Enter the 4-digit OTP sent to ${email}`
                    : `Enter the 4-digit OTP sent to +91 ${phoneNumber}`
            }
            style={{ width: "calc(100vw - 32px)", maxWidth: "489px", minWidth: 0 }}
            actions={
                <APButton
                    type="primary"
                    size="large"
                    style={{
                        borderRadius: "8px",
                        backgroundColor: APPalette["brand-300"],
                        width: "100%",
                        opacity: isOtpFilled && !submitting && !isLockedOut ? 1 : 0.6,
                    }}
                    onClick={isOtpFilled && !submitting && !isLockedOut ? handleContinue : undefined}
                >
                    {submitting ? "Verifying..." : "Continue"}
                </APButton>
            }
        >
            <APOtp
                digits={4}
                padding="0px"
                onChanged={(v) => setOtp(v)}
                onComplete={(v) => setOtp(v)}
                error={otpError || undefined}
            />

            <APSizedBox height="12px" />
            <APRow gap="4px">
                <APText variant="paragraph-small" color={APPalette["grey-800"]}>
                    Didn't get a code?
                </APText>
                {isLockedOut ? (
                    <APText variant="link-medium" color={APPalette["grey-400"]}>
                        Resend OTP in {formatTime(lockoutRemaining)}
                    </APText>
                ) : time ? (
                    <APText variant="link-medium" color={APPalette["grey-400"]}>
                        Resend OTP in {formatTime(time)}
                    </APText>
                ) : (
                    <APText
                        variant="link-medium"
                        color={resending ? APPalette["grey-400"] : APPalette["brand-300"]}
                        onClick={resending ? undefined : handleResendOTP}
                    >
                        {resending ? "Sending..." : "Resend OTP"}
                    </APText>
                )}
            </APRow>
            <APSizedBox height="12px" />
        </APDialogCard>
    );
}

// --- Helpers ---

function formatTime(ms: number): string {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

